import React, { useRef } from 'react';
import { bool, func } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  ExternalLink,
  InlineTextButton,
  IconSpinner,
  Logo,
} from '../../../components';
import { useMyContextFunctions } from '../../../context/ContextFunctions';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../context/configurationContext';
import { pathByRouteName } from '../../../util/routes';
import { LOGIN } from '../../../util/enums';
import signupBg from '../../../assets/signup-bg.webp';

import css from './SignupForm.module.css';

const SignupFormComponent = props => {
  const lastCheckedValue = useRef(null);
  const lastCheckPromise = useRef(null);
  const { onSetAuthType } = useMyContextFunctions();
  const routeConfiguration = useRouteConfiguration();
  // The site's own address for the shop link (staging shows staging's)
  const { marketplaceRootURL } = useConfiguration();
  const marketplaceHost = (() => {
    try {
      return new URL(marketplaceRootURL).host;
    } catch (e) {
      return 'tradmarket.com';
    }
  })();

  // Username availability: the check waits for typing to pause (in the duck), and a
  // username's answer is remembered. A check that failed says nothing: it is asked again.
  const usernameAvailable = async value => {
    const { onCheckUsername } = props;

    // First run synchronous validations immediately
    const syncError = validators.composeValidators(
      validators.required(
        props.intl.formatMessage({
          id: 'SignupForm.usernameRequired',
        })
      ),
      validators.minLength(
        props.intl.formatMessage({ id: 'SignupForm.usernameTooShort' }, { minLength: 3 }),
        3
      ),
      validators.maxLength(
        props.intl.formatMessage({ id: 'SignupForm.usernameTooLong' }, { maxLength: 30 }),
        30
      ),
      validators.noSpaces(
        props.intl.formatMessage({
          id: 'SignupForm.usernameInvalid',
        })
      ),
      validators.pattern(
        props.intl.formatMessage({
          id: 'SignupForm.usernameInvalid',
        }),
        /^[a-zA-Z0-9_-]+$/
      )
    )(value);

    // If sync validation fails, return error immediately
    if (syncError) {
      return syncError;
    }

    // Only check availability if sync validation passes
    if (onCheckUsername && value && value.trim()) {
      const trimmedValue = value.trim();

      // If we already checked this exact value, return the cached result
      if (lastCheckedValue.current === trimmedValue && lastCheckPromise.current) {
        return lastCheckPromise.current;
      }

      const forget = () => {
        if (lastCheckedValue.current === trimmedValue) {
          lastCheckedValue.current = null;
          lastCheckPromise.current = null;
        }
      };
      const checkPromise = Promise.resolve(onCheckUsername(trimmedValue)).then(
        isAvailable => {
          // null: a newer check replaced this one, and answers for the field
          if (isAvailable === null) forget();
          return isAvailable === false
            ? props.intl.formatMessage({ id: 'SignupForm.usernameTaken' })
            : undefined;
        },
        () => {
          forget();
          return props.intl.formatMessage({ id: 'SignupForm.usernameCheckFailed' });
        }
      );

      lastCheckedValue.current = trimmedValue;
      lastCheckPromise.current = checkPromise;
      return checkPromise;
    }

    return undefined;
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          contentClassName,
          titleClassName,
          formId,
          handleSubmit,
          inProgress,
          isModal,
          invalid,
          intl,
          renderLoginOrSignupError,
          values,
        } = fieldRenderProps;

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );

        // The modal keeps its own look; the sign-up page (AuthLayout) gives the heading, the
        // errors, the social logins and the "Already have an account?" line itself.
        const classes = classNames(rootClassName || (isModal ? css.root : css.pageRoot), className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        const emailField = (
          <FieldTextInput
            type="email"
            id={formId ? `${formId}.email` : 'email'}
            name="email"
            autoComplete="email"
            label={intl.formatMessage({
              id: 'SignupForm.emailLabel',
            })}
            placeholder={intl.formatMessage({
              id: 'SignupForm.emailPlaceholder',
            })}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />
        );

        const usernamePlaceholder = intl.formatMessage({
          id: 'SignupForm.usernamePlaceholder',
        });
        const typedUsername = values?.username ? values.username.trim() : '';
        const usernameField = (
          <div className={isModal ? css.username : null}>
            <label
              className={css.usernameLabel}
              htmlFor={formId ? `${formId}.username` : 'username'}
            >
              {intl.formatMessage({
                id: 'SignupForm.usernameLabel',
              })}
            </label>
            <div className={css.usernameWrapper}>
              <Field type="text" name="username" validate={usernameAvailable} validateFields={[]}>
                {({ input, meta }) => (
                  <>
                    <FieldTextInput
                      className={css.usernameInput}
                      type="text"
                      id={formId ? `${formId}.username` : 'username'}
                      name="username"
                      autoComplete="username"
                      placeholder={usernamePlaceholder}
                      input={input}
                      meta={meta}
                    />
                    {meta.validating && (
                      <div className={css.usernameSpinner}>
                        <IconSpinner />
                      </div>
                    )}
                  </>
                )}
              </Field>
            </div>
            {isModal ? null : (
              <p className={css.usernameHelp}>
                <FormattedMessage
                  id="SignupForm.usernameHelp"
                  values={{ host: marketplaceHost, username: typedUsername || usernamePlaceholder }}
                />
              </p>
            )}
          </div>
        );

        const nameFields = (
          <div className={isModal ? css.name : css.pageName}>
            <FieldTextInput
              className={isModal ? css.firstNameRoot : css.pageNameField}
              type="text"
              id={formId ? `${formId}.fname` : 'fname'}
              name="fname"
              autoComplete="given-name"
              label={intl.formatMessage({
                id: 'SignupForm.firstNameLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.firstNamePlaceholder',
              })}
              validate={validators.required(
                intl.formatMessage({
                  id: 'SignupForm.firstNameRequired',
                })
              )}
            />
            <FieldTextInput
              className={isModal ? css.lastNameRoot : css.pageNameField}
              type="text"
              id={formId ? `${formId}.lname` : 'lname'}
              name="lname"
              autoComplete="family-name"
              label={intl.formatMessage({
                id: 'SignupForm.lastNameLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.lastNamePlaceholder',
              })}
              validate={validators.required(
                intl.formatMessage({
                  id: 'SignupForm.lastNameRequired',
                })
              )}
            />
          </div>
        );

        // Both the modal and the page say what signing up accepts. The pages open in a new tab,
        // so what's typed stays.
        const termsLine = (
          <p className={css.terms}>
            <FormattedMessage
              id="SignupForm.termsLine"
              values={{
                termsLink: (
                  <ExternalLink
                    className={css.termsLink}
                    href={pathByRouteName('TermsOfServicePage', routeConfiguration)}
                  >
                    <FormattedMessage id="AuthenticationPage.termsAndConditionsTermsLinkText" />
                  </ExternalLink>
                ),
                privacyLink: (
                  <ExternalLink
                    className={css.termsLink}
                    href={pathByRouteName('PrivacyPolicyPage', routeConfiguration)}
                  >
                    <FormattedMessage id="AuthenticationPage.termsAndConditionsPrivacyLinkText" />
                  </ExternalLink>
                ),
              }}
            />
          </p>
        );

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div
              className={classNames(isModal ? css.signupContent : css.pageContent, contentClassName)}
            >
              {isModal ? (
                <h2 className={classNames(css.title, titleClassName)}>
                  <FormattedMessage id="SignupForm.signupTitle" />
                </h2>
              ) : null}
              {isModal ? (
                <>
                  {emailField}
                  {usernameField}
                  {nameFields}
                </>
              ) : (
                <>
                  {nameFields}
                  {usernameField}
                  {emailField}
                </>
              )}
              <FieldTextInput
                className={isModal ? css.password : null}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />
              {/* Honeypot — hidden from humans, bots fill it, server blocks them */}
              <Field name="honeypot" type="text">
                {({ input }) => (
                  <input
                    {...input}
                    type="text"
                    autoComplete="off"
                    tabIndex="-1"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: '-9999px',
                      left: '-9999px',
                      opacity: 0,
                      height: 0,
                      width: 0,
                    }}
                  />
                )}
              </Field>
              {isModal || renderLoginOrSignupError ? (
                <div className={css.errorWrapper}>{renderLoginOrSignupError}</div>
              ) : null}
              <div className={isModal ? css.bottomWrapper : css.pageBottom}>
                {termsLine}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  {isModal ? (
                    <FormattedMessage id="SignupForm.signUp" />
                  ) : (
                    <FormattedMessage id="SignupForm.createAccount" />
                  )}
                </PrimaryButton>
              </div>
              {isModal ? (
                <span className={css.helperText}>
                  <FormattedMessage
                    id="SignupForm.alreadyHaveAccount"
                    values={{
                      loginLink: (
                        <InlineTextButton
                          className={css.loginLink}
                          type="button"
                          onClick={() => onSetAuthType(LOGIN)}
                        >
                          <FormattedMessage id="SignupForm.loginLink" />
                        </InlineTextButton>
                      ),
                    }}
                  />
                </span>
              ) : null}
            </div>
            {isModal ? (
              <div
                className={css.signupInfoSec}
                style={{ backgroundImage: `url(${signupBg})` }}
              >
                <h2 className={css.signupHeading}>
                  <FormattedMessage id="SignupForm.tagline" />
                </h2>
                <div className={css.signupLogo}>
                  <Logo layout="desktop" />
                </div>
              </div>
            ) : null}
          </Form>
        );
      }}
    />
  );
};

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  onCheckUsername: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
